import { useCallback, useEffect, useState } from 'react';
import { Button } from '@/components/ui/button.jsx';
import { Input } from '@/components/ui/input.jsx';
import { toast } from '@/components/ui/toaster.jsx';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog.jsx';
import { formatDateTime } from '@/lib/date-display.js';
import useMe from '@/hooks/useMe.js';
import { ownerPagesVisible } from '@/lib/session.js';
import {
  listUsers, createUser, updateUser, disableUser, enableUser, resetGoogle, searchPeopleToLink, setUserPermissions, usersWords, ROLES,
} from '@/services/admin-users.service.js';

// TRACON USERS (personal-logins L5, PLAN 4 and R2). Owner only: the server refuses everyone else
// (403 owner_only); this page only hides itself. Every write goes through the users doors and is
// followed by a re-read of the list before the page says anything (a confirmation is a read).
// Disabling takes a reason in the same action and signs the person out on their next request.

const ROLE_WORDS = { owner: 'Owner', producer: 'Producer', demo: 'Demo (refused on the live system)' };
const SURFACE_WORDS = { browser: 'browser', standalone: 'installed app' };
const field = 'mt-1 block text-xs font-medium uppercase tracking-wide text-text-muted';

function RolePicker({ value, onChange }) {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="mt-1.5 block min-h-[44px] w-full rounded-card border border-border bg-bg px-3 text-sm text-text"
    >
      {ROLES.map((r) => <option key={r} value={r}>{ROLE_WORDS[r]}</option>)}
    </select>
  );
}

// The person a user is linked to (final-fix COMPLETE-9): searched by name or email, never typed as an
// id. Each hit shows its CRM contact and how many producer licenses sit on it, the link it makes.
const licenseWords = (p) => (p.crm_contact_id ? `CRM contact ${p.crm_contact_id}, ${p.license_count} license${p.license_count === 1 ? '' : 's'}` : 'No CRM contact, so no licenses can link');
function PersonPicker({ value, label, onPick }) {
  const [q, setQ] = useState('');
  const [hits, setHits] = useState([]);
  const [state, setState] = useState('idle');
  const [problem, setProblem] = useState('');
  useEffect(() => {
    const term = q.trim();
    if (term.length < 2) { setHits([]); setState('idle'); return undefined; }
    let live = true;
    setState('searching');
    const t = setTimeout(async () => {
      try {
        const r = await searchPeopleToLink(term);
        if (live) { setHits((r && r.people) || []); setState('done'); }
      } catch (e) {
        if (live) { setHits([]); setProblem(usersWords(e, { read: true })); setState('failed'); }
      }
    }, 300);
    return () => { live = false; clearTimeout(t); };
  }, [q]);
  return (
    <div className="mt-1.5 normal-case tracking-normal">
      <div className="flex items-center justify-between gap-2 text-sm font-normal text-text">
        <span>{value ? (label || 'Linked to a person record') : 'Not linked'}</span>
        {value ? <Button variant="outline" size="sm" onClick={() => onPick('', '')}>Unlink</Button> : null}
      </div>
      <Input className="mt-1.5" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search people by name or email" />
      {state === 'searching' ? <p className="mt-1 text-[12.5px] font-normal text-text-muted">Searching...</p> : null}
      {state === 'failed' ? <p className="mt-1 text-[12.5px] font-normal text-warning">{problem}</p> : null}
      {state === 'done' && !hits.length ? <p className="mt-1 text-[12.5px] font-normal text-text-muted">Nobody matches that.</p> : null}
      {hits.length ? (
        <ul className="mt-1.5 max-h-48 overflow-y-auto rounded-card border border-border">
          {hits.map((p) => (
            <li key={p.person_id}>
              <button
                type="button"
                onClick={() => { onPick(p.person_id, `${p.name || p.email || 'Unnamed person'} (${licenseWords(p)})`); setQ(''); }}
                className={`block min-h-[44px] w-full px-3 py-2 text-left text-sm font-normal hover:bg-surface-strong ${p.person_id === value ? 'bg-accent-soft' : ''}`}
              >
                <span className="block text-text">{p.name || 'Unnamed person'}{p.email ? `, ${p.email}` : ''}</span>
                <span className="block text-[12.5px] text-text-muted">{licenseWords(p)}</span>
              </button>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}

function UserDialog({ open, onOpenChange, user, onSave }) {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [role, setRole] = useState('producer');
  const [person, setPerson] = useState('');
  const [personLabel, setPersonLabel] = useState('');
  useEffect(() => {
    if (!open) return;
    setEmail(user ? user.email : '');
    setName(user ? user.display_name || '' : '');
    setRole(user ? user.role : 'producer');
    setPerson(user ? user.person_id || '' : '');
    setPersonLabel('');
  }, [open, user]);

  function save() {
    if (!user) return onSave({ email: email.trim().toLowerCase(), display_name: name.trim(), role, person_id: person.trim() });
    const patch = {};
    if (name.trim() !== (user.display_name || '')) patch.display_name = name.trim();
    if (role !== user.role) patch.role = role;
    if (person.trim() !== (user.person_id || '')) patch.person_id = person.trim() || null;
    if (!Object.keys(patch).length) { onOpenChange(false); return undefined; }
    return onSave(patch);
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{user ? `Edit ${user.email}` : 'Add a user'}</DialogTitle>
          <DialogDescription>
            {user ? 'The address never changes; a new address is a new user.' : 'They sign in with Google at this ndemnifly.com address. Nobody gets the shared password.'}
          </DialogDescription>
        </DialogHeader>
        {!user && (
          <label className={field}>
            Email
            <Input type="email" autoCapitalize="none" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="name@ndemnifly.com" />
          </label>
        )}
        <label className={field}>
          Name
          <Input value={name} onChange={(e) => setName(e.target.value)} />
        </label>
        <label className={field}>
          Role
          <RolePicker value={role} onChange={setRole} />
        </label>
        <div className={field}>
          Person record (links their licenses)
          <PersonPicker value={person} label={personLabel} onPick={(id, words) => { setPerson(id); setPersonLabel(words); }} />
        </div>
        <DialogFooter className="flex gap-2 pt-2">
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={save} disabled={!name.trim() || (!user && !email.trim())}>{user ? 'Save' : 'Add user'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function DisableDialog({ user, onOpenChange, onDisable }) {
  const [reason, setReason] = useState('');
  useEffect(() => { setReason(''); }, [user]);
  return (
    <Dialog open={!!user} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Disable {user ? user.display_name || user.email : ''}</DialogTitle>
          <DialogDescription>
            Their next request is refused and they are sent to the sign-in page. Say why; the record keeps it.
          </DialogDescription>
        </DialogHeader>
        <label className={field}>
          Reason
          <textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={3}
            className="mt-1.5 block w-full rounded-card border border-border bg-bg px-3 py-2 text-sm text-text"
          />
        </label>
        <DialogFooter className="flex gap-2 pt-2">
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={() => onDisable(user, reason)} disabled={!reason.trim()}>Disable</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ResetDialog({ user, onOpenChange, onReset }) {
  return (
    <Dialog open={!!user} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Reset Google for {user ? user.email : ''}</DialogTitle>
          <DialogDescription>
            Their next Google sign-in binds whichever Google account signs in as this address. Use it when the account was replaced on purpose.
          </DialogDescription>
        </DialogHeader>
        <DialogFooter className="flex gap-2 pt-2">
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={() => onReset(user)}>Reset</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// The boxes (David 2026-10-27): one checkbox per privilege, presets that fill them and can then be
// edited. The catalog and presets come from the server's list (utils/privileges.js), never a copy here.
// An owner holds every privilege; their boxes show ticked and cannot be changed. Managing users is not
// a box. Saving goes through the owner-only permissions door and takes effect on the person's next request.
function PermissionsPanel({ u, catalog, onSave }) {
  const owner = u.role === 'owner';
  const stored = Array.isArray(u.permissions) ? u.permissions : [];
  const [picked, setPicked] = useState(stored);
  useEffect(() => { setPicked(Array.isArray(u.permissions) ? u.permissions : []); }, [u.permissions]);
  if (!catalog || !catalog.privileges) return null;
  const keys = catalog.privileges.map((p) => p.key);
  const ordered = (l) => keys.filter((k) => l.includes(k));
  const dirty = ordered(picked).join(',') !== ordered(stored).join(',');
  const toggle = (k) => setPicked((l) => (l.includes(k) ? l.filter((x) => x !== k) : [...l, k]));
  return (
    <div className="mt-2.5 rounded-card border border-border p-2.5">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-xs font-medium uppercase tracking-wide text-text-muted">Privileges</span>
        {!owner && Object.keys(catalog.presets || {}).map((name) => (
          <Button key={name} variant="outline" size="sm" className="h-8" onClick={() => setPicked([...(catalog.presets[name] || [])])}>
            {(catalog.preset_labels && catalog.preset_labels[name]) || name} preset
          </Button>
        ))}
      </div>
      {owner ? <p className="mt-1 text-[12.5px] text-text-muted">An owner holds every privilege. Change the role to change them.</p> : null}
      <ul className="mt-1.5 flex flex-col gap-1">
        {catalog.privileges.map((p) => (
          <li key={p.key}>
            <label className="flex min-h-[44px] cursor-pointer items-start gap-2.5 py-1 text-sm text-text">
              <input type="checkbox" className="mt-1 h-4 w-4" checked={owner || picked.includes(p.key)} disabled={owner} onChange={() => toggle(p.key)} />
              <span>
                <span className="block">{p.label}</span>
                <span className="block text-[12.5px] text-text-muted">{p.about}</span>
              </span>
            </label>
          </li>
        ))}
      </ul>
      {!owner && dirty ? (
        <div className="mt-1.5 flex gap-2">
          <Button className="h-9" onClick={() => onSave(u, ordered(picked))}>Save privileges</Button>
          <Button variant="outline" className="h-9" onClick={() => setPicked(stored)}>Undo</Button>
        </div>
      ) : null}
    </div>
  );
}

function UserRow({ u, catalog, onEdit, onDisable, onEnable, onReset, onPermissions }) {
  const active = u.status === 'active';
  const last = u.last_login_at ? `${formatDateTime(u.last_login_at)}${u.last_login_surface ? `, ${SURFACE_WORDS[u.last_login_surface] || u.last_login_surface}` : ''}` : 'never';
  return (
    <div className="rounded-card border border-border bg-surface p-3">
      <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
        <span className="text-sm font-semibold text-text">{u.display_name || u.email}</span>
        <span className="text-[12px] text-text-muted">{u.email}</span>
        <span className="text-[12px] text-text-muted">{ROLE_WORDS[u.role] || u.role}</span>
        <span className={active ? 'text-[12px] text-text-muted' : 'text-[12px] font-semibold text-accent'}>{active ? 'Active' : 'Disabled'} ({u.status})</span>
      </div>
      <p className="mt-1 text-[12.5px] text-text-muted">Last sign-in: {last}</p>
      <p className="mt-1 text-[12.5px] text-text-muted">Person record: {u.person_id ? 'linked' : 'not linked'}</p>
      {!active && (
        <p className="mt-1 text-[12.5px] text-text-muted">
          Disabled {formatDateTime(u.disabled_at)}{u.disabled_by ? ` by ${u.disabled_by}` : ''}: {u.disabled_reason || 'no reason recorded'}
        </p>
      )}
      <PermissionsPanel u={u} catalog={catalog} onSave={onPermissions} />
      <div className="mt-2.5 flex flex-wrap gap-2">
        <Button variant="outline" className="h-9" onClick={() => onEdit(u)}>Edit</Button>
        {active
          ? <Button variant="outline" className="h-9" onClick={() => onDisable(u)}>Disable</Button>
          : <Button variant="outline" className="h-9" onClick={() => onEnable(u)}>Enable</Button>}
        <Button variant="outline" className="h-9" onClick={() => onReset(u)}>Reset Google</Button>
      </div>
    </div>
  );
}

export default function UsersAdmin() {
  const me = useMe();
  const [users, setUsers] = useState(null);
  const [catalog, setCatalog] = useState(null);
  const [error, setError] = useState('');
  const [editing, setEditing] = useState(null); // { user } or { user: null } for add
  const [disabling, setDisabling] = useState(null);
  const [resetting, setResetting] = useState(null);

  const load = useCallback(async () => {
    try {
      const d = await listUsers();
      setUsers(Array.isArray(d && d.users) ? d.users : []);
      setCatalog(d && Array.isArray(d.privileges) ? { privileges: d.privileges, presets: d.presets || {}, preset_labels: d.preset_labels || {} } : null);
      setError('');
    } catch (e) {
      setError(usersWords(e, { read: true }));
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  // Every write, then the list re-read, then the words.
  async function act(fn, done) {
    let res;
    try { res = await fn(); } catch (e) { toast.error(usersWords(e)); return; }
    try {
      const d = await listUsers();
      setUsers(Array.isArray(d && d.users) ? d.users : []);
      toast.success(done);
    } catch {
      toast.info(`${done} The list could not be read back; reload to check it.`);
    }
    return res;
  }

  async function onSave(body) {
    const user = editing && editing.user;
    const ok = await act(() => (user ? updateUser(user.id, body) : createUser(body)), user ? 'Saved.' : 'User added.');
    if (ok) setEditing(null);
  }
  async function onDisable(u, reason) {
    const res = await act(() => disableUser(u.id, reason), `${u.display_name || u.email} is disabled.`);
    if (!res) return;
    setDisabling(null);
    if (res.shared_login_still_open) toast.info('The shared login is still open: anyone holding the shared password can still sign in. Close it on the Sign-in page.');
  }
  async function onPermissions(u, permissions) {
    await act(() => setUserPermissions(u.id, permissions), `Privileges for ${u.display_name || u.email} saved. They apply on their next request.`);
  }
  async function onReset(u) {
    if (await act(() => resetGoogle(u.id), `The Google binding for ${u.email} is reset.`)) setResetting(null);
  }

  if (me && !ownerPagesVisible(me)) {
    return <div className="p-4 text-sm text-text-muted">Only the owner sees this page.</div>;
  }

  return (
    <div className="max-w-3xl p-4">
      <div className="flex items-baseline gap-3">
        <h2 className="text-lg font-semibold text-text">Users</h2>
        <Button className="ml-auto h-9" onClick={() => setEditing({ user: null })}>Add a user</Button>
      </div>
      <p className="mt-1 text-sm text-text-muted">Everyone who signs in to TRACON with Google. Everyone works the whole book. Only an owner changes users, their privileges and the sign-in switch; each person's boxes decide the rest.</p>
      {error && <p className="mt-3 text-sm text-accent">{error}</p>}
      {!users && !error && <p className="mt-3 text-sm text-text-muted">Loading...</p>}
      <div className="mt-4 flex flex-col gap-3">
        {(users || []).map((u) => (
          <UserRow key={u.id} u={u} catalog={catalog}
            onPermissions={onPermissions}
            onEdit={(x) => setEditing({ user: x })}
            onDisable={setDisabling}
            onEnable={(x) => act(() => enableUser(x.id), `${x.display_name || x.email} is enabled.`)}
            onReset={setResetting} />
        ))}
        {users && !users.length && <p className="text-sm text-text-muted">No users yet.</p>}
      </div>
      <UserDialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)} user={editing && editing.user} onSave={onSave} />
      <DisableDialog user={disabling} onOpenChange={(o) => !o && setDisabling(null)} onDisable={onDisable} />
      <ResetDialog user={resetting} onOpenChange={(o) => !o && setResetting(null)} onReset={onReset} />
    </div>
  );
}
